// apps/widget/src/lib/quality.ts
//
// The client-side quality gate. Runs live against the preview so the visitor is
// corrected BEFORE they take the photo, not after.
//
// Thresholds come from @skinvizo/shared, identical to the server's — a gate
// that passes here and fails there is the worst version of this feature.
//
// Every failure returns a specific fix. "Move toward a window, it's a little
// dark" does more for perceived accuracy than any prompt tuning.

import { QUALITY_GATE } from '@skinvizo/shared';
import type { FaceGeometry } from './facemesh';

export type QualityIssue =
  | 'NO_FACE'
  | 'MULTIPLE_FACES'
  | 'TOO_FAR'
  | 'TOO_CLOSE'
  | 'OFF_ANGLE'
  | 'TILTED'
  | 'TOO_DARK'
  | 'TOO_BRIGHT'
  | 'UNEVEN_LIGHT'
  | 'BLURRY';

export interface QualityCheck {
  passing: boolean;
  issues: QualityIssue[];
  /** The single most useful instruction right now. */
  hint: string;
  measurements: { brightness: number; evenness: number; sharpness: number };
}

/** Ordered by which is most worth fixing first. */
const HINTS: Record<QualityIssue, string> = {
  NO_FACE: 'Position your face inside the oval',
  MULTIPLE_FACES: 'Make sure only your face is in frame',
  TOO_FAR: 'Move a little closer',
  TOO_CLOSE: 'Move back slightly',
  OFF_ANGLE: 'Look straight at the camera',
  TILTED: 'Hold your head level',
  TOO_DARK: "It's a little dark — try facing a window",
  TOO_BRIGHT: 'Too bright — step out of direct light',
  UNEVEN_LIGHT: 'The light is stronger on one side',
  BLURRY: 'Hold still for a moment',
};

const PRIORITY: QualityIssue[] = [
  'NO_FACE',
  'MULTIPLE_FACES',
  'TOO_FAR',
  'TOO_CLOSE',
  'OFF_ANGLE',
  'TILTED',
  'TOO_DARK',
  'TOO_BRIGHT',
  'UNEVEN_LIGHT',
  'BLURRY',
];

export function checkFrame(video: HTMLVideoElement, geometry: FaceGeometry | null): QualityCheck {
  const issues: QualityIssue[] = [];
  const pixels = sampleFrame(video);

  if (!geometry) {
    return {
      passing: false,
      issues: ['NO_FACE'],
      hint: HINTS.NO_FACE,
      measurements: { brightness: pixels.brightness, evenness: 1, sharpness: 0 },
    };
  }

  if (geometry.faceCount > QUALITY_GATE.MAX_FACES) issues.push('MULTIPLE_FACES');

  if (geometry.heightFraction < QUALITY_GATE.FACE_HEIGHT_MIN) issues.push('TOO_FAR');
  else if (geometry.heightFraction > QUALITY_GATE.FACE_HEIGHT_MAX) issues.push('TOO_CLOSE');

  if (
    Math.abs(geometry.yaw) > QUALITY_GATE.MAX_YAW_DEG ||
    Math.abs(geometry.pitch) > QUALITY_GATE.MAX_PITCH_DEG
  ) {
    issues.push('OFF_ANGLE');
  }
  if (Math.abs(geometry.roll) > QUALITY_GATE.MAX_ROLL_DEG) issues.push('TILTED');

  if (pixels.brightness < QUALITY_GATE.BRIGHTNESS_MIN) issues.push('TOO_DARK');
  else if (pixels.brightness > QUALITY_GATE.BRIGHTNESS_MAX) issues.push('TOO_BRIGHT');

  if (pixels.evenness < QUALITY_GATE.EVENNESS_MIN || pixels.evenness > QUALITY_GATE.EVENNESS_MAX) {
    issues.push('UNEVEN_LIGHT');
  }
  if (pixels.sharpness < QUALITY_GATE.SHARPNESS_MIN) issues.push('BLURRY');

  const primary = PRIORITY.find((i) => issues.includes(i));

  return {
    passing: issues.length === 0,
    issues,
    hint: primary ? HINTS[primary] : 'Hold still',
    measurements: pixels,
  };
}

/**
 * Sampled at 160px wide. The full frame at 30fps would peg a mid-range phone,
 * and none of these statistics need the resolution.
 */
function sampleFrame(video: HTMLVideoElement): {
  brightness: number;
  evenness: number;
  sharpness: number;
} {
  const width = 160;
  const height = Math.max(1, Math.round((video.videoHeight / video.videoWidth) * width));

  const canvas = getScratchCanvas(width, height);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx || video.videoWidth === 0) return { brightness: 0, evenness: 1, sharpness: 0 };

  ctx.drawImage(video, 0, 0, width, height);
  const { data } = ctx.getImageData(0, 0, width, height);

  const luma = new Float32Array(width * height);
  let sum = 0;
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    // Rec. 601 — perceptual, so a green cast does not read as bright.
    const value = 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!;
    luma[p] = value;
    sum += value;
  }
  const brightness = sum / luma.length;

  // Left/right ratio catches the side lamp that reads as pigmentation on one
  // cheek and oiliness on the other — two wrong findings from one light.
  let left = 0;
  let right = 0;
  const half = Math.floor(width / 2);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (x < half) left += luma[y * width + x]!;
      else right += luma[y * width + x]!;
    }
  }
  const evenness = right === 0 ? 1 : left / right;

  // Laplacian variance. Crisp edges produce high variance; a blurred frame has
  // almost none.
  let lapSum = 0;
  let lapSq = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const value =
        -4 * luma[i]! + luma[i - 1]! + luma[i + 1]! + luma[i - width]! + luma[i + width]!;
      lapSum += value;
      lapSq += value * value;
      count++;
    }
  }
  const mean = count > 0 ? lapSum / count : 0;
  const sharpness = count > 0 ? lapSq / count - mean * mean : 0;

  return { brightness, evenness, sharpness };
}

let scratch: HTMLCanvasElement | null = null;

/** One canvas, reused. Allocating per frame triggers GC pauses that show as jank. */
function getScratchCanvas(width: number, height: number): HTMLCanvasElement {
  scratch ??= document.createElement('canvas');
  if (scratch.width !== width) scratch.width = width;
  if (scratch.height !== height) scratch.height = height;
  return scratch;
}

/**
 * Requires N consecutive passing frames before enabling capture.
 *
 * Without this the button flickers on and off as the visitor breathes, which
 * reads as the software being broken rather than the framing being marginal.
 */
export function createStabiliser(required = QUALITY_GATE.STABLE_FRAMES) {
  let streak = 0;
  return {
    push(passing: boolean): boolean {
      streak = passing ? streak + 1 : 0;
      return streak >= required;
    },
    reset(): void {
      streak = 0;
    },
    progress(): number {
      return Math.min(1, streak / required);
    },
  };
}

/**
 * The three things a visitor can actually act on, each either good or not.
 * A single rotating hint tells them what to fix next; these tell them how many
 * problems are left, which is what makes the wait feel finite.
 */
export type QualityGroup = 'lighting' | 'pose' | 'position';

const GROUP_ISSUES: Record<QualityGroup, QualityIssue[]> = {
  lighting: ['TOO_DARK', 'TOO_BRIGHT', 'UNEVEN_LIGHT', 'BLURRY'],
  pose: ['OFF_ANGLE', 'TILTED'],
  position: ['NO_FACE', 'MULTIPLE_FACES', 'TOO_FAR', 'TOO_CLOSE'],
};

/**
 * Lighting judged from the frame's brightness alone — what can be said before
 * the face model is ready to locate the face.
 */
export function lightingOk(check: QualityCheck): boolean {
  const b = check.measurements.brightness;
  return b >= QUALITY_GATE.BRIGHTNESS_MIN && b <= QUALITY_GATE.BRIGHTNESS_MAX;
}

export function groupStatus(check: QualityCheck | null): Record<QualityGroup, boolean> {
  // With no face, lighting and pose were never measured — reporting them
  // "Good" told the visitor two of three things were fine when nothing was
  // checked at all.
  if (!check || check.issues.includes('NO_FACE')) {
    return { lighting: false, pose: false, position: false };
  }
  return {
    lighting: Boolean(check) && !GROUP_ISSUES.lighting.some((i) => check!.issues.includes(i)),
    pose: Boolean(check) && !GROUP_ISSUES.pose.some((i) => check!.issues.includes(i)),
    position: Boolean(check) && !GROUP_ISSUES.position.some((i) => check!.issues.includes(i)),
  };
}
